import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  CategoryDescriptions,
  PageDescriptions,
  PageTitles,
  AppUrl,
  BasePaths,
  SortFileTypes,
  SortFileOptions,
  FilesTableCols,
  FilesTableHeadings,
  FileKind,
  FileKindOptions,
} from "consts";
import {
  Card,
  CardGrid,
  DataTable,
  Div,
  Divider,
  Dropdown,
  FileCard,
  Flex,
  InfiniteList,
  InfiniteLoader,
  NoneCard,
  Page,
  SearchInput,
  Tab,
  TableButtons,
  TeamTypeOptions,
} from "library/components";
import { Category, StateFilter, TableStateEnum, TeamType } from "library/types";
import { CategoryTabs } from "views/components";
import { Bookmark, ChevronDown } from "library/assets";
import { LibraryRowRender } from "./LibraryRow";
import { useMember, useSaved } from "hooks";
import { SearchFilesRes } from "redux/apiTypes";
import { searchApi } from "redux/apis";
import { PageSize } from "library/consts";
import { isCategory } from "library/utils";

export const Library = () => {
  const navigate = useNavigate();
  const [tableState, setTableState] = useState<TableStateEnum>(
    TableStateEnum.GRID
  );

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<Category | string>(StateFilter.ALL);
  const [teamType, setTeamType] = useState<TeamType>(TeamType.BLUEPRINT);
  const [kind, setKind] = useState<FileKind | "">("");
  const [sort, setSort] = useState(SortFileTypes.NEWEST_FIRST);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [skip, setSkip] = useState(0);
  const [list, setList] = useState<SearchFilesRes>();
  const [isMounted, setIsMounted] = useState(false);
  const { saved } = useSaved();

  const { isSignedIn } = useMember();

  const { data, isLoading, isFetching } = searchApi.useSearchFilesQuery({
    category: isCategory(category) ? category : undefined,
    teamType,
    kind: kind ? kind : undefined,
    sort,
    search: search || undefined,
    skip: skip || undefined,
    isSaved: isSignedIn ? isSaved : false,
  });
  const isLoadingAll = isLoading || isFetching;

  // Infinite Loader
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    } else {
      setList(undefined);
      setSkip(0);
    }
  }, [search, category, teamType, sort, isSaved, isMounted]);

  const title = isCategory(category) ? CategoryDescriptions[category] : "";

  return (
    <Page>
      <Helmet>
        <title>{`${title || "All"} - ${PageTitles.library}`}</title>
        <meta name="description" content={PageDescriptions.library} />
        <link rel="canonical" href={`${AppUrl}/${BasePaths.library}`} />
      </Helmet>

      <InfiniteList
        data={data}
        isLoading={isFetching}
        list={list}
        setList={(d) => {
          setList(d);
        }}
      />

      <Flex gap="8px" margin="32px 0" alignCenter>
        <SearchInput
          flex="1"
          handleSearch={(v) => setSearch(v)}
          isLoading={isLoadingAll}
        />
      </Flex>

      <CategoryTabs
        category={category}
        handleCategory={(v) => setCategory(v)}
      />

      <Div
        display="block"
        alignItems="center"
        margin="8px 0"
        sm={{ display: "flex", margin: "24px 0" }}
      >
        <Div flex="1" margin="32px 0 16px" sm={{ margin: "0 24px 0 12px" }}>
          <Dropdown
            handleSelect={(v) => setTeamType(v)}
            selectValue={teamType}
            options={TeamTypeOptions}
            icon={
              <Div display="flex" marginLeft="20px">
                <ChevronDown size={32} />
              </Div>
            }
            optionsProps={{ minWidth: "280px" }}
            scrollOptionTop
          />
        </Div>

        <Div
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          margin="16px 0"
          sm={{ justifyContent: "flex-end", margin: "0" }}
        >
          <Dropdown
            handleSelect={(v) => setKind(v as FileKind)}
            selectValue={kind}
            options={FileKindOptions}
          />
          <Dropdown
            handleSelect={(v) => setSort(v)}
            selectValue={sort}
            options={SortFileOptions}
          />
          {isSignedIn && (
            <Tab
              height="40px"
              sm={{ padding: "12px" }}
              onClick={() => {
                setList(undefined);
                setIsSaved(!isSaved);
              }}
            >
              <Bookmark fill={isSaved ? "currentColor" : "none"} />
            </Tab>
          )}
          <Divider margin="0 16px" />
          <TableButtons tableState={tableState} setTableState={setTableState} />
        </Div>
      </Div>

      {tableState === TableStateEnum.GRID && list?.length === 0 && (
        <NoneCard large />
      )}

      {tableState === TableStateEnum.GRID && (
        <CardGrid>
          {list?.map((d) => (
            <FileCard
              key={d.id}
              {...d}
              icon={d.app?.icon || d.member?.icon || undefined}
              appHandle={d.app?.appHandle}
              memberHandle={d.member?.memberHandle}
              tags={d.tags.map((t) => t.name)}
              saved={saved?.savedFiles.some(
                (s) => s.fileHandle === d.fileHandle
              )}
            />
          ))}
        </CardGrid>
      )}

      {tableState === TableStateEnum.ROW && (
        <Card marginTop="32px">
          <DataTable
            cols={FilesTableCols}
            headings={FilesTableHeadings}
            data={list}
            rowData={saved?.savedFiles}
            rowRender={LibraryRowRender}
            handleRowClick={(d) => {
              navigate(`/${BasePaths.f}/${d.fileHandle}`);
            }}
          />
        </Card>
      )}

      <InfiniteLoader
        show={isFetching || data?.length === PageSize}
        callback={() => {
          if (list && isLoadingAll && data?.length === PageSize) {
            const newSkip = list.length;
            if (skip !== newSkip) {
              setSkip(newSkip);
            }
          }
        }}
      />
    </Page>
  );
};
